import _ from 'lodash';
import { useState } from 'react';
import { Divider, Button, IconButton, List, ListItem, ListItemIcon, Flex, Box, Label, Text, Icon, Tooltip, Heading } from '@vibe/core';
import { Email, Person, LearnMore, Settings, Delete } from '@vibe/icons';
import {
  type Account,
  type Accounts as AccountsType,
  type AccountCreate,
  type AccountUpdate,
  type AccountImport,
} from '@gorilla/portal-shared/src/schemas';
import { AddAccountModal } from './AddAccountModal';
import { UpdateAccountModal } from './UpdateAccountModal';
import { ImportAccountModal } from './ImportAccountModal';

type AccountsProps = {
  accounts: AccountsType;
  isAdmin: boolean;
  portalUrl: string;
  portalName: string;
  portalGoogleAuthentication: boolean;
  isExporting: boolean;
  isImportingAccount: boolean;
  onCreate: (accountCreate: AccountCreate & { overwriteExisting: boolean }) => void;
  onDelete: (account: Account) => void;
  onUpdate: (accountUpdate: AccountUpdate, accountId: string) => void;
  onExport: (account: Account) => void;
  onImport: (account: AccountImport) => void;
};

export function Accounts({
  accounts,
  isAdmin,
  portalUrl,
  portalName,
  portalGoogleAuthentication,
  isExporting,
  isImportingAccount,
  onCreate,
  onUpdate,
  onDelete,
  onExport,
  onImport,
}: AccountsProps) {
  const [deletionsInProgress, setDeletionsInProgress] = useState<string[]>([]);
  const [showAddAccountModal, setShowAddAccountModal] = useState<boolean>(false);
  const [showImportAccountModal, setShowImportAccountModal] = useState<boolean>(false);
  const [currentUpdateAccount, setCurrentUpdateAccount] = useState<Account | null>(null);

  return (
    <>
      {showAddAccountModal ? (
        <AddAccountModal
          show
          portalUrl={portalUrl}
          portalName={portalName}
          portalGoogleAuthentication={portalGoogleAuthentication}
          onCreate={async (account) => {
            await onCreate(account);
          }}
          onClose={() => {
            setShowAddAccountModal(false);
          }}
        />
      ) : null}
      {currentUpdateAccount ? (
        <UpdateAccountModal
          show
          account={currentUpdateAccount}
          onClose={async (accountUpdate) => {
            if (accountUpdate) {
              await onUpdate(accountUpdate, (currentUpdateAccount as any).id);
            }

            setCurrentUpdateAccount(null);
          }}
        />
      ) : null}
      {showImportAccountModal ? (
        <ImportAccountModal
          show
          onClose={async (accountToImport) => {
            setShowImportAccountModal(false);

            if (accountToImport) {
              await onImport(accountToImport);
            }
          }}
        />
      ) : null}
      <div>
        {accounts.length ? (
          <>
            <Box
              margin="large"
              border={true}
              borderColor="layoutBorderColor"
              rounded="small"
              backgroundColor="primaryBackgroundColor"
              shadow="xs"
            >
              <Flex gap={24} style={{ padding: '16px 16px 16px 20px' }}>
                <Flex gap={8}>
                  <Icon icon={Person} size={24} />
                  <Heading type="h3" weight="bold" align="start" maxLines={1} ellipsis={false}>
                    Login methods
                  </Heading>
                  <Tooltip
                    content="Manage the login methods for this specific client and grant access to the Client Portal."
                    position="right"
                  >
                    <Icon icon={LearnMore} size={16} style={{ transform: 'translate3d(0,1px,0)' }} />
                  </Tooltip>
                </Flex>
                <Box style={{ marginLeft: 'auto' }}>
                  <Flex gap={12}>
                    {isAdmin ? (
                      <Button
                        kind="secondary"
                        size="small"
                        loading={isImportingAccount}
                        disabled={isImportingAccount || isExporting || !!deletionsInProgress.length}
                        onClick={() => {
                          setShowImportAccountModal(true);
                        }}
                        style={{
                          paddingLeft: '13px',
                          paddingRight: '13px',
                        }}
                      >
                        Import
                      </Button>
                    ) : null}
                    <Button
                      kind="primary"
                      size="small"
                      disabled={isImportingAccount || isExporting || !!deletionsInProgress.length}
                      onClick={() => {
                        setShowAddAccountModal(true);
                      }}
                      style={{
                        paddingLeft: '13px',
                        paddingRight: '13px',
                      }}
                    >
                      Create
                    </Button>
                  </Flex>
                </Box>
              </Flex>
              <List>
                {/* List clones its direct children (tabIndex, ref, id), so they must be elements, not fragments. */}
                {accounts.flatMap((account) => [
                  <Divider key={`${(account as any).id}-divider`} withoutMargin />,
                  <ListItem
                    key={(account as any).id}
                    className="portalmanager__content__listitem portalmanager__content__listitem--noclick"
                    size="medium"
                    disabled={!account.enabled ? true : false}
                  >
                    <ListItemIcon icon={Email} className="custom__listitemicon" />
                    <Flex gap={16}>
                      <Text
                        type="text2"
                        align="center"
                        color="primary"
                        ellipsis
                        style={{
                          maxWidth:
                            account.type === 'credentials'
                              ? !account.enabled
                                ? 'calc(100vw - 340px)'
                                : 'calc(100vw - 260px)'
                              : !account.enabled
                                ? 'calc(100vw - 410px)'
                                : 'calc(100vw - 330px)',
                          opacity: !account.enabled ? 0.3 : 1,
                        }}
                      >
                        {account.email}
                      </Text>
                      <Flex gap={8}>
                        {account.type !== 'credentials' ? (
                          <Box
                            style={{
                              opacity: !account.enabled ? 0.3 : 1,
                            }}
                          >
                            <Label text={account.type === 'google' ? 'Google' : 'SSO'} color="dark" kind="line" />
                          </Box>
                        ) : null}
                        {!account.enabled ? <Label text="Disabled" color="negative" /> : null}
                      </Flex>
                    </Flex>
                    <Box style={{ marginLeft: 'auto' }}>
                      <Flex>
                        {isAdmin ? (
                          <Button
                            kind="tertiary"
                            size="small"
                            disabled={isExporting || !!deletionsInProgress.length}
                            loading={isExporting}
                            onClick={() => {
                              onExport(account);
                            }}
                            style={{
                              paddingLeft: '13px',
                              paddingRight: '13px',
                            }}
                          >
                            Export
                          </Button>
                        ) : null}
                        <IconButton
                          icon={Settings}
                          size="small"
                          kind="tertiary"
                          disabled={isExporting || !!deletionsInProgress.length}
                          onClick={() => {
                            setCurrentUpdateAccount(account);
                          }}
                          className="custom__iconbutton--small"
                        />
                        <IconButton
                          icon={Delete}
                          size="small"
                          kind="tertiary"
                          disabled={isExporting || !!deletionsInProgress.length}
                          loading={deletionsInProgress.includes((account as any).id)}
                          onClick={async () => {
                            setDeletionsInProgress([...deletionsInProgress, (account as any).id]);
                            try {
                              await onDelete(account);
                            } catch (err) {}
                            setDeletionsInProgress(deletionsInProgress.filter((id) => id !== (account as any).id));
                          }}
                          className="custom__iconbutton--small"
                        />
                        {/* 
                            <Button
                              kind="tertiary"
                              size="small"
                              disabled={isExporting || !!deletionsInProgress.length}
                              onClick={() => {
                                setCurrentUpdateAccount(account);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              kind="tertiary"
                              size="small"
                              disabled={isExporting || !!deletionsInProgress.length}
                              loading={deletionsInProgress.includes((account as any).id)}
                              onClick={async () => {
                                setDeletionsInProgress([...deletionsInProgress, (account as any).id]);
                                try {
                                  await onDelete(account);
                                } catch (err) {}
                                setDeletionsInProgress(deletionsInProgress.filter((id) => id !== (account as any).id));
                              }}
                            >
                              Delete
                            </Button>
                            */}
                      </Flex>
                    </Box>
                  </ListItem>,
                ])}
              </List>
            </Box>
          </>
        ) : (
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '24px',
              width: 'calc(100% - 50px)',
              height: 'calc(100vh - 123px)',
              minHeight: '300px',
              border: '1px dashed #D0D4E4',
              borderRadius: '4px',
            }}
          >
            <Flex
              direction="column"
              gap={16}
              style={{
                marginTop: '-32px',
              }}
            >
              <Flex direction="column" gap={4}>
                <Icon icon={Person} size={32} />
                <Heading type="h3" weight="bold" align="center">
                  No login methods
                </Heading>
              </Flex>
              <Text type="text2" align="center" color="secondary">
                Create a login method to let this client sign in to your <br />
                Client Portal and grant access to their related data.
              </Text>
              <Flex gap={16} direction="row">
                <Button
                  onClick={() => {
                    setShowAddAccountModal(true);
                  }}
                >
                  Create
                </Button>
                {isAdmin ? (
                  <Button
                    kind="secondary"
                    loading={isImportingAccount}
                    disabled={isImportingAccount || isExporting || !!deletionsInProgress.length}
                    onClick={() => {
                      setShowImportAccountModal(true);
                    }}
                  >
                    Import
                  </Button>
                ) : null}
              </Flex>
            </Flex>
          </Box>
        )}
      </div>
    </>
  );
}
